import { useMemo, useCallback, forwardRef } from 'react';

import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import PropTypes from 'prop-types';
import { BsGoogle, BsMicrosoft } from 'react-icons/bs';

import { ACTIVITY_COLORS, getActivityType } from '../../../constants/activityTypes';
import './CrmCalendar.css';

const STATUS_STYLES = {
	completed: { className: 'opacity-50' },
	cancelled: { className: 'opacity-30 line-through' },
};

const PROVIDER_META = {
	google_calendar: { icon: BsGoogle, color: '#ea4335' },
	microsoft_calendar: { icon: BsMicrosoft, color: '#0078d4' },
};

/** Custom event content renderer for FullCalendar */
function renderEventContent(eventInfo) {
	const { activity, calendarProvider } = eventInfo.event.extendedProps;
	if (!activity) return null;

	const type = getActivityType(activity.activityType);
	const Icon = type.icon;
	const companyName = activity.companyName;
	const providerMeta = calendarProvider ? PROVIDER_META[calendarProvider] : null;
	const ProviderIcon = providerMeta?.icon;

	return (
		<div className="crm-event-content">
			<Icon className="crm-event-icon" style={{ color: type.color }} />
			<div className="crm-event-text">
				<div className="crm-event-time">
					{eventInfo.timeText}
					{ProviderIcon && (
						<ProviderIcon className="crm-event-provider-icon" style={{ color: providerMeta.color }} />
					)}
				</div>
				<div className="crm-event-title">{eventInfo.event.title}</div>
				{companyName && <div className="crm-event-company">{companyName}</div>}
			</div>
		</div>
	);
}

const DEFAULT_HEADER = {
	left: 'prev,next today',
	center: 'title',
	right: 'dayGridMonth,timeGridWeek,timeGridDay',
};

/**
 * Shared FullCalendar wrapper used by both the Activities page and the Calendar page.
 * Supports forwardRef so parents can call calendarRef.current.getApi() for programmatic control.
 */
const CrmCalendar = forwardRef(
	(
		{
			activities = [],
			initialView = 'timeGridWeek',
			onEventClick,
			onDateClick,
			onDatesSet,
			height,
			headerToolbar,
			className,
		},
		ref,
	) => {
		const events = useMemo(
			() =>
				activities
					.filter((a) => a.dueAt)
					.map((a) => {
						const durationMinutes = a.channelPayload?.durationMinutes ?? 60;
						const start = new Date(a.dueAt);
						const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
						const colors = ACTIVITY_COLORS[a.activityType] ?? ACTIVITY_COLORS.task;
						const statusStyle = STATUS_STYLES[a.status] ?? {};

						return {
							id: a.id,
							title: a.title,
							start: start.toISOString(),
							end: end.toISOString(),
							backgroundColor: colors.bgColor ?? colors.backgroundColor,
							borderColor: colors.borderColor,
							textColor: colors.color,
							classNames: statusStyle.className ? [statusStyle.className] : [],
							extendedProps: {
								activity: a,
								activityType: a.activityType,
								status: a.status,
								companyName: a.companyName,
								calendarProvider: a.provider || a.calendarProvider,
							},
						};
					}),
			[activities],
		);

		const handleEventClick = useCallback(
			(info) => {
				if (onEventClick) {
					onEventClick(info.event.extendedProps.activity);
				}
			},
			[onEventClick],
		);

		const handleDateClick = useCallback(
			(info) => {
				if (onDateClick) {
					onDateClick(info.dateStr);
				}
			},
			[onDateClick],
		);

		const handleDatesSet = useCallback(
			(info) => {
				if (onDatesSet) {
					onDatesSet({ start: info.startStr, end: info.endStr });
				}
			},
			[onDatesSet],
		);

		// headerToolbar={false} hides the toolbar entirely
		const toolbar = headerToolbar === false ? false : (headerToolbar ?? DEFAULT_HEADER);

		return (
			<div className={`crm-calendar ${className ?? ''}`}>
				<FullCalendar
					ref={ref}
					plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
					initialView={initialView}
					locale="de"
					headerToolbar={toolbar}
					buttonText={{
						today: 'Heute',
						month: 'Monat',
						week: 'Woche',
						day: 'Tag',
					}}
					events={events}
					eventContent={renderEventContent}
					eventClick={handleEventClick}
					dateClick={handleDateClick}
					datesSet={handleDatesSet}
					height={height ?? 'auto'}
					nowIndicator
					slotMinTime="07:00:00"
					slotMaxTime="20:00:00"
					slotDuration="00:30:00"
					weekends
					firstDay={1}
					allDaySlot={false}
					eventTimeFormat={{
						hour: '2-digit',
						minute: '2-digit',
						hour12: false,
					}}
					slotLabelFormat={{
						hour: '2-digit',
						minute: '2-digit',
						hour12: false,
					}}
				/>
			</div>
		);
	},
);

CrmCalendar.displayName = 'CrmCalendar';

CrmCalendar.propTypes = {
	activities: PropTypes.array,
	initialView: PropTypes.string,
	onEventClick: PropTypes.func,
	onDateClick: PropTypes.func,
	onDatesSet: PropTypes.func,
	height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	headerToolbar: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
	className: PropTypes.string,
};

export default CrmCalendar;
